import { useState, useEffect, useCallback } from 'react';
import { platformFetch } from '../../../platform/http';
import { formatBeijingDate, formatBeijingDateTime } from '../../../utils/time';
import { CanvasPreviewModal } from './CanvasPreviewModal';
import {
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Eye,
  Maximize2,
  LayoutGrid,
  Loader2,
  Filter,
} from 'lucide-react';

interface CanvasProject {
  id: string;
  name: string;
  userId: string;
  userEmail: string;
  userName: string;
  viewportX: number;
  viewportY: number;
  zoom: number;
  background: string;
  theme: string;
  isHidden: boolean;
  nodeCount: number;
  edgeCount: number;
  createdAt: string;
  updatedAt: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const backgroundMap: Record<string, string> = {
  dot: '圆点',
  grid: '网格',
  blank: '空白',
};

export default function CanvasProjectsTab() {
  const [projects, setProjects] = useState<CanvasProject[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [userEmailFilter, setUserEmailFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [detailProject, setDetailProject] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [previewProjectId, setPreviewProjectId] = useState<string | null>(null);

  const fetchProjects = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (searchQuery) params.set('search', searchQuery);
      if (userEmailFilter) params.set('userEmail', userEmailFilter);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);

      const res = await platformFetch(`/admin/canvas/projects?${params}`);
      const data = await res.json();
      if (data.data) {
        setProjects(data.data);
        setPagination(data.pagination);
      }
    } catch (err) {
      console.error('获取画布项目失败:', err);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, userEmailFilter, startDate, endDate]);

  useEffect(() => { fetchProjects(1); }, [fetchProjects]);

  const handleDelete = async (id: string) => {
    if (!confirm('确定删除该画布项目？此操作不可恢复。')) return;
    try {
      const res = await platformFetch(`/admin/canvas/projects/${id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        fetchProjects(pagination.page);
      } else {
        const data = await res.json();
        alert(data.error || '删除失败');
      }
    } catch {
      alert('删除失败');
    }
  };

  const handleViewDetail = async (id: string) => {
    setDetailLoading(true);
    try {
      const res = await platformFetch(`/admin/canvas/projects/${id}`);
      const data = await res.json();
      if (data.data) setDetailProject(data.data);
    } catch {
      alert('获取详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSearch = () => fetchProjects(1);
  const handleReset = () => {
    setSearchQuery('');
    setUserEmailFilter('');
    setStartDate('');
    setEndDate('');
  };

  return (
    <div className="space-y-4 lg:space-y-6">
      {/* 搜索和筛选 */}
      <div className="bg-white rounded-2xl p-4 lg:p-6 shadow-lg border border-light-border">
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="flex-1 min-w-0">
            <label className="text-sm text-light-muted mb-1 block">项目名称</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-light-muted" />
              <input
                type="text"
                placeholder="搜索画布名称"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 rounded-xl border border-light-border text-sm focus:outline-none focus:ring-2 focus:ring-light-primary/20"
              />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <label className="text-sm text-light-muted mb-1 block">用户邮箱</label>
            <input
              type="text"
              placeholder="按邮箱筛选"
              value={userEmailFilter}
              onChange={(e) => setUserEmailFilter(e.target.value)}
              className="w-full px-4 py-2 rounded-xl border border-light-border text-sm focus:outline-none focus:ring-2 focus:ring-light-primary/20"
            />
          </div>
          <div className="w-full lg:w-44">
            <label className="text-sm text-light-muted mb-1 block">开始日期</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-2 rounded-xl border border-light-border text-sm focus:outline-none focus:ring-2 focus:ring-light-primary/20" />
          </div>
          <div className="w-full lg:w-44">
            <label className="text-sm text-light-muted mb-1 block">结束日期</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-2 rounded-xl border border-light-border text-sm focus:outline-none focus:ring-2 focus:ring-light-primary/20" />
          </div>
          <div className="flex gap-2">
            <button onClick={handleSearch}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-light-primary text-white rounded-xl hover:bg-light-primary/90 transition-colors">
              <Filter className="w-4 h-4" /><span className="hidden sm:inline">筛选</span>
            </button>
            <button onClick={handleReset}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-light-hover text-light-text rounded-xl hover:bg-light-border transition-colors">
              <RefreshCw className="w-4 h-4" /><span className="hidden sm:inline">重置</span>
            </button>
          </div>
        </div>
      </div>

      {/* 项目列表 */}
      <div className="bg-white rounded-2xl shadow-lg border border-light-border overflow-hidden">
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-light-border bg-light-hover">
                <th className="text-left text-sm font-medium text-light-muted py-3 px-4">项目名称</th>
                <th className="text-left text-sm font-medium text-light-muted py-3 px-4">用户</th>
                <th className="text-center text-sm font-medium text-light-muted py-3 px-4">节点数</th>
                <th className="text-center text-sm font-medium text-light-muted py-3 px-4">连线数</th>
                <th className="text-center text-sm font-medium text-light-muted py-3 px-4">背景</th>
                <th className="text-center text-sm font-medium text-light-muted py-3 px-4">主题</th>
                <th className="text-left text-sm font-medium text-light-muted py-3 px-4">更新时间</th>
                <th className="text-center text-sm font-medium text-light-muted py-3 px-4">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-accent-blue mx-auto" /></td></tr>
              ) : projects.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-light-muted">
                  <LayoutGrid className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>暂无画布项目</p>
                </td></tr>
              ) : projects.map((p) => (
                <tr key={p.id} className="border-b border-light-border last:border-0 hover:bg-light-hover/50">
                  <td className="py-3 px-4">
                    <p className="text-sm text-light-text font-medium">{p.name || '未命名画布'}</p>
                    {p.isHidden && <span className="text-xs text-orange-500">已隐藏</span>}
                  </td>
                  <td className="py-3 px-4">
                    <p className="text-sm text-light-text">{p.userEmail}</p>
                    {p.userName && <p className="text-xs text-light-muted">{p.userName}</p>}
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-light-text">{p.nodeCount}</td>
                  <td className="py-3 px-4 text-center text-sm text-light-text">{p.edgeCount}</td>
                  <td className="py-3 px-4 text-center">
                    <span className="px-2 py-1 rounded-lg text-xs bg-slate-50 text-slate-600">{backgroundMap[p.background] || p.background}</span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className={`px-2 py-1 rounded-lg text-xs ${p.theme === 'dark' ? 'bg-gray-800 text-gray-200' : 'bg-gray-50 text-gray-600'}`}>
                      {p.theme === 'dark' ? '暗色' : '亮色'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-light-muted">{formatBeijingDateTime(p.updatedAt)}</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => setPreviewProjectId(p.id)} className="p-2 hover:bg-light-hover rounded-lg transition-colors" title="预览画布">
                        <Maximize2 className="w-4 h-4 text-light-muted" />
                      </button>
                      <button onClick={() => handleViewDetail(p.id)} className="p-2 hover:bg-light-hover rounded-lg transition-colors" title="查看详情">
                        <Eye className="w-4 h-4 text-light-muted" />
                      </button>
                      <button onClick={() => handleDelete(p.id)} className="p-2 hover:bg-red-50 rounded-lg transition-colors" title="删除">
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden divide-y divide-light-border">
          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-accent-blue mx-auto" /></div>
          ) : projects.length === 0 ? (
            <div className="text-center py-12 text-light-muted">
              <LayoutGrid className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>暂无画布项目</p>
            </div>
          ) : projects.map((p) => (
            <div key={p.id} className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-light-text">{p.name || '未命名画布'}</p>
                  <p className="text-xs text-light-muted">{p.userEmail}</p>
                </div>
                <div className="flex gap-1 ml-2">
                  <button onClick={() => setPreviewProjectId(p.id)} className="p-1.5 hover:bg-light-hover rounded-lg" title="预览画布">
                    <Maximize2 className="w-4 h-4 text-light-muted" />
                  </button>
                  <button onClick={() => handleViewDetail(p.id)} className="p-1.5 hover:bg-light-hover rounded-lg">
                    <Eye className="w-4 h-4 text-light-muted" />
                  </button>
                  <button onClick={() => handleDelete(p.id)} className="p-1.5 hover:bg-red-50 rounded-lg">
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-light-hover rounded-lg p-2 text-center">
                  <p className="text-xs text-light-muted">节点</p>
                  <p className="text-sm font-medium text-light-text">{p.nodeCount}</p>
                </div>
                <div className="bg-light-hover rounded-lg p-2 text-center">
                  <p className="text-xs text-light-muted">连线</p>
                  <p className="text-sm font-medium text-light-text">{p.edgeCount}</p>
                </div>
                <div className="bg-light-hover rounded-lg p-2 text-center">
                  <p className="text-xs text-light-muted">更新</p>
                  <p className="text-xs text-light-text">{formatBeijingDate(p.updatedAt)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 分页 */}
        {pagination.totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between px-4 lg:px-6 py-4 border-t border-light-border gap-3">
            <p className="text-sm text-light-muted">共 {pagination.total} 条，第 {pagination.page} / {pagination.totalPages} 页</p>
            <div className="flex items-center gap-2">
              <button onClick={() => fetchProjects(pagination.page - 1)} disabled={pagination.page <= 1}
                className="flex items-center gap-1 px-3 py-2 rounded-xl border border-light-border hover:bg-light-hover text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                <ChevronLeft className="w-4 h-4" /><span className="hidden sm:inline">上一页</span>
              </button>
              <button onClick={() => fetchProjects(pagination.page + 1)} disabled={pagination.page >= pagination.totalPages}
                className="flex items-center gap-1 px-3 py-2 rounded-xl border border-light-border hover:bg-light-hover text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                <span className="hidden sm:inline">下一页</span><ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 详情弹窗 */}
      {detailProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 lg:p-6 border-b border-light-border">
              <div>
                <h3 className="text-lg font-semibold text-light-text">画布详情</h3>
                <p className="text-sm text-light-muted mt-1">{detailProject.name || '未命名画布'}</p>
              </div>
              <button onClick={() => setDetailProject(null)} className="p-2 hover:bg-light-hover rounded-lg transition-colors">
                <span className="text-light-muted text-lg">&times;</span>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 lg:p-6 space-y-4">
              {detailLoading ? (
                <div className="text-center py-8"><Loader2 className="w-8 h-8 animate-spin text-accent-blue mx-auto" /></div>
              ) : (
                <>
                  <div className="bg-light-hover rounded-xl p-4">
                    <h4 className="text-sm font-medium text-light-muted mb-3">基本信息</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div><p className="text-xs text-light-muted">用户</p><p className="text-sm text-light-text">{detailProject.user?.email}</p></div>
                      <div><p className="text-xs text-light-muted">主题</p><p className="text-sm text-light-text">{detailProject.theme === 'dark' ? '暗色' : '亮色'}</p></div>
                      <div><p className="text-xs text-light-muted">背景</p><p className="text-sm text-light-text">{backgroundMap[detailProject.background] || detailProject.background}</p></div>
                      <div><p className="text-xs text-light-muted">缩放</p><p className="text-sm text-light-text">{detailProject.zoom?.toFixed(2)}</p></div>
                      <div><p className="text-xs text-light-muted">创建时间</p><p className="text-sm text-light-text">{formatBeijingDateTime(detailProject.createdAt)}</p></div>
                      <div><p className="text-xs text-light-muted">更新时间</p><p className="text-sm text-light-text">{formatBeijingDateTime(detailProject.updatedAt)}</p></div>
                    </div>
                  </div>
                  <div className="bg-light-hover rounded-xl p-4">
                    <h4 className="text-sm font-medium text-light-muted mb-3">节点列表 ({detailProject.nodes?.length || 0})</h4>
                    <div className="space-y-2 max-h-60 overflow-auto">
                      {(detailProject.nodes || []).map((node: any) => (
                        <div key={node.id} className="flex items-center gap-3 bg-white rounded-lg p-2 text-sm">
                          <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-600 text-xs">{node.type}</span>
                          <span className="text-light-muted">位置: ({Math.round(node.x)}, {Math.round(node.y)})</span>
                          <span className="text-light-muted">尺寸: {node.width}x{node.height}</span>
                        </div>
                      ))}
                      {(!detailProject.nodes || detailProject.nodes.length === 0) && <p className="text-sm text-light-muted text-center py-4">暂无节点</p>}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 画布全屏预览 */}
      {previewProjectId && (
        <CanvasPreviewModal
          projectId={previewProjectId}
          onClose={() => setPreviewProjectId(null)}
        />
      )}
    </div>
  );
}
